import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  DollarSign, 
  CreditCard, 
  Wallet, 
  TrendingUp, 
  AlertTriangle,
  PieChart,
  Activity
} from 'lucide-react';
import api from '../services/api';
import SummaryCard from '../components/dashboard/SummaryCard';
import EnvelopeSummary from '../components/dashboard/EnvelopeSummary';
import DebtSummary from '../components/dashboard/DebtSummary';
import RecentTransactions from '../components/dashboard/RecentTransactions';
import SpendingChart from '../components/dashboard/SpendingChart';
import { PageLoading } from '../components/common/Loading';

interface DashboardData {
  summary: {
    income: {
      monthly_total: number;
    };
    budget: {
      total_budgeted: number;
      total_spent: number;
      remaining: number;
      allocation_percentage: number;
    };
    envelopes: {
      total: number;
      over_budget: number;
      on_track: number;
    };
    debt: {
      total_balance: number;
      total_minimum_payment: number;
      active_debts: number;
    };
    spending: {
      this_month: number;
      last_month: number;
      change_percentage: number;
    };
  };
  recent_transactions: Array<{
    id: number;
    date: string;
    payee: string;
    amount: number;
    type: 'expense' | 'income';
    envelope_name?: string;
  }>;
  envelope_summary: Array<{
    id: number;
    name: string;
    budgeted: number;
    spent: number;
    remaining: number;
    percentage: number;
  }>;
  debt_summary: Array<{
    id: number;
    name: string;
    current_balance: number;
    interest_rate: number;
    minimum_payment: number;
    progress: number;
  }>;
  spending_by_category: Array<{
    category: string;
    amount: number;
  }>;
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<DashboardData>('/dashboard');
      setData(response);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !data) {
    return <PageLoading />;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-danger-bg border border-danger/20 rounded-card p-6 flex items-start gap-4">
          <AlertTriangle className="h-6 w-6 text-danger flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-text-header">Error loading dashboard</h3>
            <p className="mt-1 text-text-muted">{error}</p>
            <button
              onClick={loadDashboard}
              className="mt-4 btn btn-secondary text-sm"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const availableToBudget = (summary?.income.monthly_total || 0) - (summary?.budget.total_budgeted || 0);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-header tracking-tight">Dashboard</h1>
          <p className="mt-1 text-text-muted">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/transactions" className="btn btn-secondary text-sm">
            View Transactions
          </Link>
          <Link to="/envelopes" className="btn btn-primary text-sm shadow-glow">
            Manage Budget
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          title="Monthly Income"
          value={summary?.income.monthly_total || 0}
          icon={<DollarSign className="w-5 h-5" />}
        />
        <SummaryCard
          title="Total Budgeted"
          value={summary?.budget.total_budgeted || 0}
          subtitle={`${(summary?.budget.allocation_percentage || 0).toFixed(0)}% of income`}
          icon={<Wallet className="w-5 h-5" />}
        />
        <SummaryCard
          title="Available to Budget"
          value={availableToBudget}
          valueClassName={availableToBudget >= 0 ? 'text-success' : 'text-danger'}
          icon={<PieChart className="w-5 h-5" />}
        />
        <SummaryCard
          title="Spending Trend"
          value={summary?.spending.this_month || 0}
          trend={
            summary?.spending.change_percentage !== undefined
              ? {
                  value: summary.spending.change_percentage,
                  isPositive: summary.spending.change_percentage < 0
                }
              : undefined
          }
          subtitle="vs last month"
          icon={<Activity className="w-5 h-5" />}
        />
      </div>

      {/* Debt Summary Row (if debts exist) */}
      {(summary?.debt.active_debts || 0) > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SummaryCard
            title="Total Debt"
            value={summary?.debt.total_balance || 0}
            valueClassName="text-danger"
            icon={<CreditCard className="w-5 h-5" />}
          />
          <SummaryCard
            title="Min. Monthly Payment"
            value={summary?.debt.total_minimum_payment || 0}
            icon={<TrendingUp className="w-5 h-5" />}
          />
          <SummaryCard
            title="Active Debts"
            value={`${summary?.debt.active_debts || 0}`}
            icon={<AlertTriangle className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Envelopes and Spending Chart */}
        <div className="lg:col-span-2 space-y-8">
          <SpendingChart
            data={data?.spending_by_category || []}
            isLoading={isLoading}
          />

          <EnvelopeSummary
            envelopes={data?.envelope_summary || []}
            isLoading={isLoading}
          />
        </div>

        {/* Right Column - Transactions and Debts */}
        <div className="space-y-8">
          <RecentTransactions
            transactions={data?.recent_transactions || []}
            isLoading={isLoading}
          />
          {(summary?.debt.active_debts || 0) > 0 && (
            <DebtSummary
              debts={data?.debt_summary || []}
              totalBalance={summary?.debt.total_balance || 0}
              totalMinPayment={summary?.debt.total_minimum_payment || 0}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>

      {/* Empty State - No Data Yet */}
      {!isLoading && 
       (summary?.income.monthly_total || 0) === 0 && 
       (summary?.envelopes.total || 0) === 0 && (
        <div className="bg-background-card border border-primary/20 rounded-card p-8 text-center shadow-glow">
          <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Wallet className="h-6 w-6 text-primary" />
          </div>
          <h3 className="text-xl font-bold text-text-header mb-2">Welcome to Budget!</h3>
          <p className="text-text-muted mb-6 max-w-md mx-auto">
            Get started by adding your income sources and creating budget envelopes to track your spending.
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/income" className="btn btn-primary">
              Add Income
            </Link>
            <Link to="/envelopes" className="btn btn-secondary">
              Create Envelopes
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
